import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PackagePlus, X, Search, LayoutGrid, Table2 } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import ProductCardSkeleton from "@/components/ProductCardSkeleton";
import ListingMatrix from "@/components/ListingMatrix";
import CompareMachines from "@/components/CompareMachines";
import EmmyAdvisor from "@/components/EmmyAdvisor";
import { ShowroomStrip } from "@/components/ShowroomStrip";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { intent: "", mk_primary: "", mk_subcategory: "", county: "", city: "", search: "", sort: "recent" };

export default function Listings() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tree, setTree] = useState([]);
  const [counties, setCounties] = useState([]);
  const [cities, setCities] = useState({});
  const [view, setView] = useState(() => localStorage.getItem("oxo_listings_view") || "matrix");
  const [filters, setFilters] = useState({
    ...EMPTY,
    mk_primary: searchParams.get("mk_primary") || "",
    intent: searchParams.get("intent") || "",
  });
  const [searchInput, setSearchInput] = useState("");
  const [compare, setCompare] = useState([]);

  const toggleCompare = (l) => setCompare((c) => c.find((x) => x.id === l.id) ? c.filter((x) => x.id !== l.id) : (c.length >= 3 ? c : [...c, l]));

  const SORTS = [
    { v: "recent", l: t("common.sortRecent", "Cele mai recente") },
    { v: "price_asc", l: t("common.sortPriceAsc", "Preț crescător") },
    { v: "price_desc", l: t("common.sortPriceDesc", "Preț descrescător") },
  ];

  useEffect(() => {
    api.get("/marketplace-taxonomy").then((r) => setTree(r.data.tree)).catch(() => {});
    api.get("/ro-locations").then((r) => { setCounties(r.data.counties); setCities(r.data.cities || {}); }).catch(() => {});
  }, []);

  useEffect(() => { localStorage.setItem("oxo_listings_view", view); }, [view]);

  useEffect(() => {
    const id = setTimeout(() => setFilters((f) => ({ ...f, search: searchInput.trim() })), 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  const fetchListings = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filters.intent) params.intent = filters.intent;
    if (filters.mk_subcategory) params.mk_subcategory = filters.mk_subcategory;
    else if (filters.mk_primary) params.mk_primary = filters.mk_primary;
    if (filters.county) params.county = filters.county;
    if (filters.city) params.city = filters.city;
    if (filters.search) params.search = filters.search;
    if (filters.sort && filters.sort !== "recent") params.sort = filters.sort;
    api.get("/listings", { params }).then((r) => setListings(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const reset = () => { setFilters(EMPTY); setSearchInput(""); };
  const countyCities = useMemo(() => (filters.county && cities[filters.county]) || [], [filters.county, cities]);
  const hasActive = filters.intent || filters.mk_primary || filters.mk_subcategory || filters.county || filters.city || filters.search;

  const INTENT_LABEL = { oferta: t("capital.intentOffer"), cerere: t("capital.intentRequest"), caut_investor: t("capital.intentSeekInvestor"), vreau_sa_investesc: t("capital.intentInvest") };
  const chips = [];
  if (filters.intent) chips.push({ key: "intent", label: INTENT_LABEL[filters.intent] || filters.intent, clear: () => setFilters((f) => ({ ...f, intent: "" })) });
  if (filters.mk_primary) { const g = tree.find((x) => x.id === filters.mk_primary); chips.push({ key: "primary", label: g?.name || "Categorie", clear: () => setFilters((f) => ({ ...f, mk_primary: "", mk_subcategory: "" })) }); }
  if (filters.mk_subcategory) { const g = tree.find((x) => x.id === filters.mk_primary); const s = g?.subcategories?.find((x) => x.id === filters.mk_subcategory); chips.push({ key: "sub", label: s?.name || "Subcategorie", clear: () => setFilters((f) => ({ ...f, mk_subcategory: "" })) }); }
  if (filters.county) chips.push({ key: "county", label: filters.county, clear: () => setFilters((f) => ({ ...f, county: "", city: "" })) });
  if (filters.city) chips.push({ key: "city", label: filters.city, clear: () => setFilters((f) => ({ ...f, city: "" })) });
  if (filters.search) chips.push({ key: "search", label: `„${filters.search}”`, clear: () => { setSearchInput(""); setFilters((f) => ({ ...f, search: "" })); } });

  const subs = tree.find((g) => g.id === filters.mk_primary)?.subcategories || [];

  return (
    <div className="w-full max-w-full overflow-x-hidden mx-auto px-2 sm:px-3 py-3" data-testid="listings-page">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight leading-none">Anunțuri de la utilizatori</h1>
          <p className="text-slate-500 text-sm mt-0.5">Utilaje, materiale și servicii — verificate de OXO.</p>
        </div>
        <Link to={user ? "/vinde" : "/autentificare"} className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2.5 font-bold uppercase tracking-wide text-xs transition-colors shrink-0" data-testid="sell-cta">
          <PackagePlus className="w-4 h-4" /> Adaugă un anunț
        </Link>
      </div>

      {/* Compact horizontal filter bar */}
      <div className="border border-slate-200 bg-slate-50 p-2.5 mb-3" data-testid="listings-filters">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Caută titlu, brand, locație..."
              className="w-full pl-8 pr-3 py-2 bg-white border border-slate-300 text-sm focus:outline-none focus:border-orange-500" data-testid="listings-search" />
          </div>
          <Select value={filters.mk_primary || "__all"} onValueChange={(v) => setFilters((f) => ({ ...f, mk_primary: v === "__all" ? "" : v, mk_subcategory: "" }))}>
            <SelectTrigger className="rounded-none bg-white w-auto min-w-[150px] h-[38px]" data-testid="filter-cat"><SelectValue placeholder="Toate categoriile" /></SelectTrigger>
            <SelectContent className="max-h-[50vh]">
              <SelectItem value="__all">Toate categoriile</SelectItem>
              {tree.map((g) => <SelectItem key={g.id} value={g.id} data-testid={`filter-cat-${g.id}`}>{g.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {subs.length > 0 && (
            <Select value={filters.mk_subcategory || "__all"} onValueChange={(v) => setFilters((f) => ({ ...f, mk_subcategory: v === "__all" ? "" : v }))}>
              <SelectTrigger className="rounded-none bg-white w-auto min-w-[150px] h-[38px]" data-testid="filter-subcat"><SelectValue placeholder="Toate subcategoriile" /></SelectTrigger>
              <SelectContent className="max-h-[50vh]">
                <SelectItem value="__all">Toate subcategoriile</SelectItem>
                {subs.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`filter-subcat-${s.id}`}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={filters.county || "__all"} onValueChange={(v) => setFilters((f) => ({ ...f, county: v === "__all" ? "" : v, city: "" }))}>
            <SelectTrigger className="rounded-none bg-white w-auto min-w-[130px] h-[38px]" data-testid="filter-county"><SelectValue placeholder="Toate județele" /></SelectTrigger>
            <SelectContent className="max-h-[50vh]">
              <SelectItem value="__all">Toate județele</SelectItem>
              {counties.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          {filters.county && (
            <>
              <input list="listings-city-list" value={filters.city} onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))}
                placeholder="Localitate" className="px-3 py-2 bg-white border border-slate-300 text-sm w-[140px] focus:outline-none focus:border-orange-500 h-[38px]" data-testid="filter-city" />
              <datalist id="listings-city-list">{countyCities.map((c) => <option key={c} value={c} />)}</datalist>
            </>
          )}
          <Select value={filters.sort} onValueChange={(v) => setFilters((f) => ({ ...f, sort: v }))}>
            <SelectTrigger className="rounded-none bg-white w-auto min-w-[150px] h-[38px]" data-testid="listings-sort"><SelectValue /></SelectTrigger>
            <SelectContent>{SORTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
          </Select>

          {/* View toggle */}
          <div className="flex border border-slate-300 bg-white h-[38px] ml-auto" data-testid="listings-view-toggle">
            <button onClick={() => setView("matrix")} className={`px-2.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${view === "matrix" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid="view-matrix"><Table2 className="w-4 h-4" /> {t("ux.view.matrix")}</button>
            <button onClick={() => setView("grid")} className={`px-2.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${view === "grid" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid="view-grid"><LayoutGrid className="w-4 h-4" /> {t("ux.view.grid")}</button>
          </div>
        </div>

        {/* Intent quick filters + active chips */}
        <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
          {[{ v: "", l: t("capital.all") }, { v: "oferta", l: t("capital.intentOffer") }, { v: "cerere", l: t("capital.intentRequest") }, { v: "caut_investor", l: t("capital.intentSeekInvestor") }, { v: "vreau_sa_investesc", l: t("capital.intentInvest") }].map((o) => (
            <button key={o.v || "all"} onClick={() => setFilters((f) => ({ ...f, intent: o.v }))}
              className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1.5 border transition-colors ${filters.intent === o.v ? "border-orange-600 bg-orange-600 text-white" : "border-slate-300 text-slate-600 hover:border-slate-400 bg-white"}`}
              data-testid={`filter-intent-${o.v || "all"}`}>{o.l}</button>
          ))}
          {chips.length > 0 && <span className="w-px h-5 bg-slate-300 mx-1" />}
          {chips.map((c) => (
            <button key={c.key} onClick={c.clear} className="inline-flex items-center gap-1 bg-slate-200 hover:bg-slate-300 text-slate-700 text-[11px] font-semibold px-2 py-1.5 transition-colors" data-testid={`chip-${c.key}`}>{c.label} <X className="w-3 h-3" /></button>
          ))}
          {hasActive && <button onClick={reset} className="text-[11px] text-orange-600 font-bold hover:underline ml-1" data-testid="reset-filters">Resetează</button>}
        </div>
      </div>

      {!hasActive && <div className="mb-3"><ShowroomStrip /></div>}

      {/* Split: 65% dense matrix / 35% sticky Emmy sidebar */}
      <div className="grid grid-cols-1 xl:grid-cols-[65fr_35fr] gap-3 items-start">
        <div>
          <div className="text-xs text-slate-500 mb-2 font-mono" data-testid="listings-count">{loading ? "Se încarcă..." : `${listings.length} anunțuri`}</div>
          {loading ? (
            view === "matrix" ? (
              <div className="border border-slate-200 divide-y divide-slate-100">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-14 bg-slate-50 animate-pulse" />)}</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">{Array.from({ length: 6 }).map((_, i) => <ProductCardSkeleton key={i} />)}</div>
            )
          ) : listings.length === 0 ? (
            <div className="border border-slate-200 p-10 text-center" data-testid="no-public-listings">
              <p className="text-slate-500">{t("ux.matrix.empty")}</p>
              {hasActive ? <button onClick={reset} className="inline-block mt-4 text-orange-600 font-semibold hover:underline">Resetează filtrele</button>
                : <Link to={user ? "/vinde" : "/autentificare"} className="inline-block mt-4 text-orange-600 font-semibold hover:underline">Fii primul care publică</Link>}
            </div>
          ) : view === "matrix" ? (
            <ListingMatrix listings={listings} selectedIds={compare.map((c) => c.id)} onToggleCompare={toggleCompare} compareFull={compare.length >= 3} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {listings.map((l, i) => <ProductCard key={l.id} product={l} priority={i < 4} />)}
            </div>
          )}
        </div>

        {/* Emmy — permanent strategic partner (Trader persona) */}
        <aside className="hidden xl:block sticky top-[120px] self-start h-[calc(100vh-136px)] overflow-y-auto" data-testid="emmy-rail">
          <EmmyAdvisor variant="sidebar" />
        </aside>
      </div>

      <CompareMachines items={compare} onRemove={toggleCompare} onClear={() => setCompare([])} />
    </div>
  );
}
